import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Token storage key constant
const TOKEN_KEY = 'auth_token';
const USER_KEY = 'user';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
// Note: For enhanced security in production, consider using httpOnly cookies
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  me: () => api.get('/auth/me'),
  registerWithInvite: (data) => api.post('/auth/register-with-invite', data),
};

// Team
export const teamApi = {
  getMembers: () => api.get('/team/members'),
  invite: (data) => api.post('/team/invite', data),
  getInvites: () => api.get('/team/invites'),
  deleteInvite: (id) => api.delete(`/team/invites/${id}`),
  removeMember: (id) => api.delete(`/team/members/${id}`),
  updateRole: (id, role) => api.put(`/team/members/${id}/role?role=${role}`),
  changePassword: (userId, newPassword) => api.put(`/team/members/${userId}/password`, { user_id: userId, new_password: newPassword }),
};

// Vendors
export const vendorApi = {
  getAll: () => api.get('/vendors'),
  getById: (id) => api.get(`/vendors/${id}`),
  create: (data) => api.post('/vendors', data),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
};

// Products
export const productApi = {
  getAll: (vendorId) => api.get('/products', { params: vendorId ? { vendor_id: vendorId } : {} }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Inventory
export const inventoryApi = {
  getAll: () => api.get('/inventory'),
  getLowStock: () => api.get('/inventory/low-stock'),
  update: (productId, data) => api.put(`/inventory/${productId}`, data),
};

// Installers
export const installerApi = {
  getAll: () => api.get('/installers'),
  create: (data) => api.post('/installers', data),
  update: (id, data) => api.put(`/installers/${id}`, data),
  delete: (id) => api.delete(`/installers/${id}`),
};

// Purchase Orders
export const purchaseOrderApi = {
  getAll: (status, vendorId) => api.get('/purchase-orders', { 
    params: { 
      ...(status && { status }), 
      ...(vendorId && { vendor_id: vendorId }) 
    } 
  }),
  getById: (id) => api.get(`/purchase-orders/${id}`),
  create: (data, notifyVendor = false) => api.post(`/purchase-orders?notify_vendor=${notifyVendor}`, data),
  update: (id, data) => api.put(`/purchase-orders/${id}`, data),
  updateStatus: (id, data) => api.patch(`/purchase-orders/${id}/status`, data),
  delete: (id) => api.delete(`/purchase-orders/${id}`),
  downloadPdf: (id) => api.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' }),
  notifyVendor: (id) => api.post(`/purchase-orders/${id}/notify-vendor`),
};

// Installations
export const installationApi = {
  getAll: (status, installerId) => api.get('/installations', { 
    params: { 
      ...(status && { status }), 
      ...(installerId && { installer_id: installerId }) 
    } 
  }),
  getById: (id) => api.get(`/installations/${id}`),
  create: (data) => api.post('/installations', data),
  update: (id, data) => api.put(`/installations/${id}`, data),
  updateStatus: (id, data) => api.patch(`/installations/${id}/status`, data),
  delete: (id) => api.delete(`/installations/${id}`),
  uploadAttachment: (id, file, fileType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    return api.post(`/installations/${id}/attachments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAttachment: (installationId, attachmentId) => api.delete(`/installations/${installationId}/attachments/${attachmentId}`),
  getAttachment: (installationId, attachmentId) => api.get(`/installations/${installationId}/attachments/${attachmentId}`),
  sendEmail: (id) => api.post(`/installations/${id}/send-email`),
  downloadPdf: (id) => api.get(`/installations/${id}/pdf`, { responseType: 'blob' }),
};

// Customers
export const customerApi = {
  getAll: () => api.get('/customers'),
  getById: (id) => api.get(`/customers/${id}`),
  create: (data) => api.post('/customers', data),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
  getInstallations: (id) => api.get(`/customers/${id}/installations`),
};

// Analytics
export const analyticsApi = {
  getSummary: () => api.get('/analytics/summary'),
};

export default api;
